import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Container } from '../components/layout/Primitives';
import { Button } from '../components/ui/Button';
import { USE_API } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { clearAuthError, loginThunk, selectAuthReady } from '../store/actions/authActions';

const PageWrap = styled.div`
  padding: clamp(2.5rem, 6vw, 4rem) 0 clamp(4rem, 10vw, 6rem);
  min-height: min(70vh, 720px);
`;

const Shell = styled.div`
  max-width: 28rem;
  margin: 0 auto;
`;

const Title = styled.h1`
  margin: 0 0 0.45rem;
  font-family: ${({ theme }) => theme.fonts.display};
  font-size: clamp(1.5rem, 2vw + 1rem, 2rem);
  font-weight: 300;
  text-align: center;
  letter-spacing: -0.02em;
  color: ${({ theme }) => theme.colors.foreground};
`;

const Lead = styled.p`
  margin: 0 0 2rem;
  text-align: center;
  font-size: ${({ theme }) => theme.type.bodySm};
  line-height: 1.55;
  color: ${({ theme }) => theme.colors.mutedForeground};
`;

const Field = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.type.bodyXs};
  font-weight: 500;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  margin-bottom: 0.5rem;
  color: ${({ theme }) => theme.colors.foreground};
`;

const AuthInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 0.75rem 1rem;
  border: 1px solid ${({ theme }) => theme.colors.border};
  background: transparent;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.foreground};
  outline: none;

  &:focus {
    border-color: ${({ theme }) => theme.colors.foreground};
  }
`;

const ErrorText = styled.p`
  margin: 0 0 1rem;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.terracottaDark};
`;

const Footer = styled.p`
  margin-top: 1.5rem;
  text-align: center;
  font-size: ${({ theme }) => theme.type.bodySm};
  color: ${({ theme }) => theme.colors.mutedForeground};

  a {
    text-decoration: underline;
    color: inherit;
    font-weight: 500;
  }
`;

function safePostAuthPath(redirect) {
  if (!redirect || typeof redirect !== 'string') return '/account';
  const d = decodeURIComponent(redirect.trim());
  return d.startsWith('/') && !d.startsWith('//') ? d : '/account';
}

export default function Login() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const loggedIn = useAppSelector(selectAuthReady);
  const status = useAppSelector((s) => s.auth?.status ?? 'idle');
  const error = useAppSelector((s) => s.auth?.error);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const redirectParam = searchParams.get('redirect');
  const from = useMemo(() => {
    if (redirectParam) return safePostAuthPath(redirectParam);
    const st = location.state?.from?.pathname;
    return st?.startsWith('/') && !st.startsWith('//') ? st : '/account';
  }, [redirectParam, location.state?.from?.pathname]);

  const registerTo = useMemo(() => {
    if (!redirectParam) return '/register';
    return `/register?redirect=${encodeURIComponent(redirectParam)}`;
  }, [redirectParam]);

  useEffect(() => {
    dispatch(clearAuthError());
  }, [dispatch]);

  useEffect(() => {
    if (loggedIn) navigate(from, { replace: true });
  }, [loggedIn, from, navigate]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!USE_API) return;
    dispatch(loginThunk({ email: email.trim(), password }));
  };

  const busy = status === 'loading';

  return (
    <div className="animate-fade-in">
      <PageWrap>
        <Container>
          <Shell>
            <Title>{t('modamai.auth.loginTitle')}</Title>
            <Lead>{t('modamai.auth.loginLead')}</Lead>

            {!USE_API ? (
              <Lead style={{ marginBottom: 0 }}>{t('modamai.auth.loginApiOff')}</Lead>
            ) : (
              <form onSubmit={handleSubmit} noValidate>
                {error ? (
                  <ErrorText role="alert">
                    {typeof error === 'string' ? error : t('modamai.auth.signInFailed')}
                  </ErrorText>
                ) : null}
                <Field>
                  <Label htmlFor="login-email">{t('modamai.checkout.fieldEmail')}</Label>
                  <AuthInput
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={busy}
                  />
                </Field>
                <Field>
                  <Label htmlFor="login-password">{t('modamai.checkout.fieldPassword')}</Label>
                  <AuthInput
                    id="login-password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={busy}
                  />
                </Field>
                <Button type="submit" $variant="luxe" $size="lg" style={{ width: '100%', marginTop: '0.25rem' }} disabled={busy}>
                  {busy ? t('modamai.auth.signingIn') : t('modamai.auth.signIn')}
                </Button>
                <Footer>
                  {t('modamai.auth.noAccount')}{' '}
                  <Link to={registerTo}>{t('modamai.auth.createOne')}</Link>
                </Footer>
              </form>
            )}
          </Shell>
        </Container>
      </PageWrap>
    </div>
  );
}
